/**
 * Optional Performance API marks for diagnosing WebContainer / preview latency.
 *
 * **Off by default.** Enable by setting `ideozWorkbenchPerf` to `'1'` in either:
 * - `sessionStorage`, or
 * - `localStorage`
 *
 * Inspect timings in DevTools → Performance, or via `performance.getEntriesByType('measure')`.
 */
export function isWorkbenchPerfEnabled(): boolean {
  if (typeof globalThis.window === 'undefined') return false;
  try {
    return (
      globalThis.window.sessionStorage?.getItem?.('ideozWorkbenchPerf') === '1' ||
      globalThis.window.localStorage?.getItem?.('ideozWorkbenchPerf') === '1'
    );
  } catch {
    return false;
  }
}

export function wcPerfMark(label: string): void {
  if (!isWorkbenchPerfEnabled() || typeof globalThis.performance?.mark !== 'function') return;
  try {
    performance.mark(label);
  } catch {
    /* noop */
  }
}

/** End mark; records a measure and clears marks (no console output). */
export function wcPerfMeasure(markStart: string, markEnd: string, measureLabel: string): void {
  if (!isWorkbenchPerfEnabled() || typeof globalThis.performance?.measure !== 'function') return;
  try {
    performance.measure(measureLabel, markStart, markEnd);
    performance.clearMarks(markEnd);
    performance.clearMarks(markStart);
  } catch {
    /* noop */
  }
}

/** Run async work and emit one Performance measure named `measureName` (internally collision-safe). */
export async function wcPerfAsync<T>(measureName: string, fn: () => Promise<T>): Promise<T> {
  const id =
    typeof globalThis.performance?.now === 'function'
      ? `id-${performance.now()}-${Math.random().toString(36).slice(2, 8)}`
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const markStart = `${measureName}:${id}:start`;
  const markEnd = `${measureName}:${id}:end`;
  wcPerfMark(markStart);
  try {
    return await fn();
  } finally {
    wcPerfMark(markEnd);
    wcPerfMeasure(markStart, markEnd, measureName);
  }
}
